/** 風向の数値コード (0–15) を日本語の16方位名に変換する */
const WIND_DIRECTION_NAMES: readonly string[] = [
  '北北東', // 0
  '北東',   // 1
  '東北東', // 2
  '東',     // 3
  '東南東', // 4
  '南東',   // 5
  '南南東', // 6
  '南',     // 7
  '南南西', // 8
  '南西',   // 9
  '西南西', // 10
  '西',     // 11
  '西北西', // 12
  '北西',   // 13
  '北北西', // 14
  '北',     // 15
] as const;

/**
 * 風向の入力値を 0–15 の整数コードに正規化する。
 *
 * @param direction - 数値または数値文字列の風向コード。
 * @returns 0–15 の整数コード、または変換できない場合は `undefined`
 */
export function normalizeWindDirectionCode(direction: unknown): number | undefined {
  if (direction == null) return undefined;

  if (typeof direction === 'string') {
    const trimmed = direction.trim();
    if (trimmed === '') return undefined;

    const num = Number(trimmed);
    if (!Number.isInteger(num) || num < 0 || num > 15) return undefined;

    return num;
  }

  const num = Number(direction);
  if (!Number.isInteger(num) || num < 0 || num > 15) return undefined;

  return num;
}

/**
 * 風向の数値コードを日本語の方位名に変換する。
 *
 * @param direction - 風向コード (0–15)。数値以外や範囲外の場合は `undefined` を返す。
 * @returns 日本語の16方位名、または変換できない場合は `undefined`
 */
export function mapWindDirection(direction: unknown): string | undefined {
  const num = normalizeWindDirectionCode(direction);
  if (num === undefined) return undefined;

  return WIND_DIRECTION_NAMES[num];
}

/**
 * 風向コードを、風が流れる向きの時計回り角度に変換する。
 *
 * 既存データのコード体系は 15=北、0=北北東、1=北東... のため、
 * まず「風が吹いてくる方角」の角度を算出し、矢印表示では180度反転する。
 */
export function getWindDirectionDegrees(direction: unknown): number {
  const code = normalizeWindDirectionCode(direction);
  if (code === undefined) return 0;

  const sourceDegrees = code === 15 ? 0 : (code + 1) * 22.5;
  return (sourceDegrees + 180) % 360;
}

/** 北方位角を CSS 回転に使える 0–359 の整数値へ正規化する。 */
export function normalizeNorthAngle(angle: unknown): number {
  if (angle == null) return 0;

  const num = Number(angle);
  if (!Number.isFinite(num)) return 0;

  return ((Math.round(num) % 360) + 360) % 360;
}

/** 風向コードと現場の北方位角を合成し、コンパス矢印の回転角を返す。 */
export function getWindCompassDegrees(direction: unknown, northAngle: unknown): number {
  return (getWindDirectionDegrees(direction) + normalizeNorthAngle(northAngle)) % 360;
}
